<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">Create Stop</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>

        <div v-if="!isSales">
            <md-button class="md-success chargeButton-toggle" @click="openCharges()">
                Charges ({{ chargeList.length }})
            </md-button>
            <charge-list-modal
                :rates-list="chargeList"
                :rates-is-loading="ratesIsLoading"
                :rate-list-data="rateListData"
                :currency="currency"
                :rule-data="chargeRule"
            />
        </div>
        <br />

        <div class="modal-body stop-form-modal">
            <form-wrapper :validator="$v.stopDetails" class="form-wrapper">
                <div class="stop-form-container">
                    <div v-if="!isSales">
                        <charging-type-options
                            :selected-charge-type-id="stopDetails.rateGroupId"
                            @selectedOption="handleRateTypeChanged"
                        />

                        <team-region-member-controls
                            :team-region-id="stopDetails.teamRegionId"
                            :public-user-id="stopDetails.assignToPublicUserId"
                            :carrier-team-id="stopDetails.carrierTeamId"
                            @onChanged="handleTeamRegionMemberChanged"
                            :strict-team-region-filter="false"
                            :show-carriers="false"
                            :self-assign="isSales"
                            :display-availability-indicator="canDisplayAvailabilityIndicators"
                            :available-team-member-ids="availableTeamMemberIds"
                        />

                        <div class="two-column">
                            <form-group name="sourceReference" label="Source Reference">
                                <md-input v-model="stopDetails.sourceReference" />
                            </form-group>
                            <form-group name="type" label="Type">
                                <md-input v-model="stopDetails.type" type="hidden" />
                                <vue-select
                                    data-testid="stop-type"
                                    :options="stopTypes"
                                    :reduce="(x) => x.type"
                                    label="key"
                                    v-model="stopDetails.type"
                                ></vue-select>
                            </form-group>
                        </div>
                    </div>
                    <div class="contact-details">
                        <div :class="brandList.length > 0 ? 'two-column' : 'one-column'">
                            <customer-autocomplete
                                :label="stopDetails.customerId === null ? 'Customer / Contact Name' : 'Customer'"
                                id="autocustomer"
                                placeholder=""
                                :should-focus="false"
                                v-model="stopDetails"
                                :auto-fill-address="true"
                                :update-customer="true"
                                :allow-create-customer="true"
                                version="v2"
                                @change="handleCustomerFieldChange"
                                @changeCustomerRateGroup="handleCustomerChangeRateGroup"
                                @createCustomer="handleCreateCustomer"
                                :disabled="disableCustomerField"
                            />
                            <brand-filter v-model="stopDetails" v-if="!isSales" @brand="handleBrandListUpdated" />
                        </div>
                        <form-group name="name" label="Contact Name" v-if="stopDetails.customerId !== null">
                            <md-input v-model="stopDetails.contact.name" />
                        </form-group>
                        <div>
                            <google-autocomplete
                                label="Destination"
                                ref="address"
                                id="autoaddress"
                                classname="form-control autocomplete-input"
                                placeholder=""
                                :should-focus="false"
                                v-model="stopDetails"
                                :disable-gps-coordinates="false"
                                @handleCoordinates="handleCoordinates"
                                @change="handleStopDetailsChanged"
                                version="v2"
                                @frequentAddressSelected="handleFrequentAddressSelected"
                            ></google-autocomplete>
                        </div>
                        <div class="two-column">
                            <form-group name="phone" label="Phone">
                                <md-input v-model="stopDetails.contact.phone" />
                            </form-group>
                            <form-group name="email" label="Email">
                                <md-input v-model="stopDetails.contact.email" />
                            </form-group>
                        </div>
                        <div v-if="loadFieldList !== null && !isSales">
                            <load-inputs
                                v-for="(load, index) in loadFieldList"
                                :key="index"
                                :load-definition="load"
                                :initial-value="loadValues"
                                @changed="handleLoadChanged"
                            />
                        </div>
                        <form-group v-if="skillOptions.length && !isSales" name="skills" label="Skills">
                            <multiselect
                                data-testid="skills-option"
                                v-model="stopDetails.skills"
                                :options="skillOptions"
                                :multiple="true"
                                :close-on-select="true"
                                placeholder="Pick required skills"
                            ></multiselect>
                        </form-group>
                        <form-group name="notes" label="Notes">
                            <md-textarea v-model="stopDetails.notes" />
                        </form-group>
                        <div v-if="stopCustomFieldList !== null && !isSales">
                            <custom-field-inputs
                                v-for="(customField, index) in stopCustomFieldList"
                                :key="index"
                                :custom-field-definition="customField"
                                :initial-value="customFieldValues[customField.name]"
                                :touched="isCustomFieldsTouched"
                                @changed="handleCustomFieldChanged"
                            />
                        </div>
                        <div v-if="!isCustomerAdmin && !isSales" class="two-column">
                            <form-group name="runNumber" label="Run Number (Optional)">
                                <md-input
                                    :disabled="stopDetails.runName !== null && stopDetails.runName !== ''"
                                    v-model="stopDetails.runNumber"
                                    @input="($value) => (stopDetails.runNumber = parseFloat($value) || null)"
                                    type="number"
                                    min="1"
                                />
                            </form-group>
                            <form-group
                                v-feature="FEATURE_NAMES.TripRuns"
                                v-if="
                                    stopDetails.assignToPublicUserId !== null &&
                                        stopDetails.assignToPublicUserId !== undefined &&
                                        stopDetails.assignToPublicUserId !== 'Unassigned' &&
                                        stopDetails.assignToPublicUserId !== 0
                                "
                                name="runName"
                                label="Run Name (Optional)"
                            >
                                <md-input
                                    :disabled="stopDetails.runNumber !== null && stopDetails.runNumber !== ''"
                                    v-model="stopDetails.runName"
                                    @input="($value) => (stopDetails.runName = $value)"
                                    max="50"
                                />
                            </form-group>
                        </div>
                    </div>
                    <div class="tripdate">
                        <md-checkbox
                            v-if="!isSales"
                            data-testid="trip-date-checkbox"
                            v-model="isSetTripDate"
                            :disabled="tripDateRequired"
                            @change="handleIsSetTripDateChange($event)"
                        >
                            Set trip date for this stop?
                        </md-checkbox>
                    </div>
                    <div
                        class="stop-datepicker"
                        v-if="isSetTripDate"
                        :class="{
                            'hide-clear-button': stopDetails.assignToPublicUserId,
                            'md-layout trip-date': $root.isMobileOnly
                        }"
                        ref="bottomElement"
                    >
                        <div :class="{ 'md-layout-item md-xsmall-size-100': $root.isMobileOnly }">
                            <md-datepicker
                                ref="mdDatePicker"
                                v-model="stopDetails.tripDate"
                                md-immediately
                                :md-debounce="10"
                            >
                                <label>Trip Date</label>
                            </md-datepicker>
                            <span
                                v-if="isSetTripDate && tripDateRequired && stopDetails.tripDate == null"
                                class="error"
                            >
                                Trip date is required.
                            </span>
                            <trip-date-in-past :trip-date="stopDetails.tripDate" job="Stop" />
                        </div>
                        <div :class="{ 'md-layout-item md-xsmall-size-100': $root.isMobileOnly }">
                            <form-group name="time" label="Time" class="time-picker">
                                <time-picker
                                    :time="time"
                                    @selectedTime="getTime"
                                    :enable-auto-scroll="true"
                                    :initial-time-window-start="stopDetails.timeWindowStart"
                                    :initial-time-window-end="stopDetails.timeWindowEnd"
                                />
                            </form-group>
                        </div>
                        <div :class="{ 'md-layout-item md-xsmall-size-100': $root.isMobileOnly }">
                            <form-group name="durationMinutes" label="Duration (mins)">
                                <md-input type="number" v-model="stopDetails.durationMinutes" />
                            </form-group>
                        </div>
                    </div>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-primary" :disabled="ratesIsLoading" @click="createStop">
                Create
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
        </div>
    </div>
</template>

<script>
import { required, numeric, requiredIf, maxLength } from 'vuelidate/lib/validators';
import moment from 'moment';
import { mapGetters } from 'vuex';
import marked from 'marked';
import { handleRequests, showErrorMessage, isAValidCoordinate, showTeamRegionControl } from '@/helpers';
import { STOP_DETAILS_DEFAULTS } from '@/utils/defaults';
import {
    TimePicker,
    CustomFieldInputs,
    LoadInputs,
    CustomerAutocomplete,
    TeamRegionMemberControls,
    ChargeListModal,
    ChargingTypeOptions,
    BrandFilter,
    TripDateInPast
} from '@/components';
import { BrandsMixin, GeneralMixin, RatesEngineMixin, CustomFieldValidationMixin } from '@/mixins';
import { DATE_TYPES, STOP_TYPES, CREATE_STOP_REQUIRED_BOOKING_FIELDS, FEATURE_NAMES } from '@/utils/constants';
import { ReverseBitwise } from '@/utils/ApplyRatesToUtils';
import GoogleAutocomplete from '@/components/Autocomplete/GoogleAutocomplete';
import { RosteringMixin } from '@/mixins/RosteringMixin';

export default {
    name: 'CreateStopModal',
    components: {
        GoogleAutocomplete,
        TimePicker,
        CustomFieldInputs,
        LoadInputs,
        CustomerAutocomplete,
        TeamRegionMemberControls,
        ChargeListModal,
        ChargingTypeOptions,
        BrandFilter,
        TripDateInPast
    },
    mixins: [GeneralMixin, RatesEngineMixin, BrandsMixin, RosteringMixin, CustomFieldValidationMixin],
    props: {
        stopDetails: {
            type: Object,
            default: () => {}
        },
        members: {
            type: Array,
            default: () => []
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        createCustomer: {
            type: Function,
            default: () => {}
        },
        disableCustomerField: {
            type: Boolean,
            default: false
        },
        displayAvailabilityIndicator: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            isCustomerAdmin: 'user/isCustomerAdmin',
            hasAssetAccess: 'user/hasAssetAccess',
            teamRegions: 'team/teamRegions',
            isSingleUser: 'user/isIndividualUser',
            isSingleTeamMember: 'team/isSingleTeamMember',
            isSingleTeamMemberUser: 'user/isSingleTeamMember',
            isSupportUser: 'user/isSupportUser',
            hasRateRules: 'user/hasRateRules',
            isSales: 'user/isSales'
        }),
        showTeamRegion() {
            return showTeamRegionControl(
                {
                    user: this.user,
                    hasAssetAccess: this.hasAssetAccess,
                    teamRegions: this.teamRegions
                },
                this.stopDetails.teamRegionId
            );
        },
        canDisplayAvailabilityIndicators() {
            return this.$_canDisplayAvailabilityIndicators(
                this.displayAvailabilityIndicator,
                this.stopDetails.tripDate
            );
        }
    },
    created() {
        const tempMembers = this.members.map((member) => ({
            publicUserId: member.publicUserId,
            fullName: `${member.firstName || ''} ${member.lastName || ''}`,
            email: member.email,
            teamRegionId: member.teamRegionId,
            teamId: member.teamId
        }));

        this.teamMemberOptions = [...tempMembers];
    },
    async mounted() {
        this.stopDetails.teamRegionId = this.user.teamRegionId;
        if (
            // eslint-disable-next-line eqeqeq
            (this.stopDetails.assignToPublicUserId !== null && this.stopDetails.assignToPublicUserId !== 0) ||
            this.isSingleTeamMemberUser
        ) {
            this.isSetTripDate = true;
            this.tripDateRequired = true;
        }

        this.stopCustomFieldList = this.user.stopCustomFieldDefinitions;
        if (this.stopCustomFieldList !== null && this.stopCustomFieldList.length && this.stopDetails.customFields) {
            this.customFieldValues = this.stopDetails.customFields;
        }
        if (this.user.skillOptions) {
            this.skillOptions = this.user.skillOptions.map((x) => x.name);
        }

        if (
            (this.isSingleUser || this.isSingleTeamMember || this.hideTeamRegion) &&
            !this.isCustomerAdmin &&
            !this.isSupportUser
        ) {
            this.stopDetails.assignToPublicUserId = this.user.publicUserId;
        }

        this.loadFieldList = this.user.vehicleCapacityUnitsConfiguration || [];
        if (this.loadFieldList !== null && this.loadFieldList.length && this.stopDetails.load) {
            this.loadValues = this.stopDetails.load;
        }

        this.retrieveRequiredFlags();
        if (this.canDisplayAvailabilityIndicators) {
            await this.$_getAvailableTeamMembers(this.stopDetails.tripDate);
        }
    },
    watch: {
        isSetTripDate(newValue, oldValue) {
            // eslint-disable-next-line eqeqeq
            if (newValue) {
                this.scrollToBottom();
                if (!this.$root.isDesktop) {
                    this.$nextTick(() => {
                        if (this.$refs.mdDatePicker)
                            this.$refs.mdDatePicker.$refs.input.$el.setAttribute('readonly', true);
                    });
                }
                this.stopDetails.tripDate = moment().format(DATE_TYPES.internationalDate);
            } else {
                this.stopDetails.appointmentTime = null;
                this.stopDetails.tripDate = null;
            }
        },
        'stopDetails.assignToPublicUserId': function(newValue, oldValue) {
            if (!this.isFrequentAddressSelected) {
                // eslint-disable-next-line eqeqeq
                if (newValue !== null && newValue != 0) {
                    this.isSetTripDate = true;
                    this.tripDateRequired = true;
                    this.setDurationMinutes(newValue);
                } else {
                    this.tripDateRequired = false;
                    this.stopDetails.runName = null;
                }
            }
        },
        'stopDetails.tripDate': async function(newValue, oldValue) {
            // eslint-disable-next-line eqeqeq
            if (newValue !== oldValue) {
                this.validateCanGenerateRates();
                if (this.canDisplayAvailabilityIndicators) {
                    await this.$_getAvailableTeamMembers(this.stopDetails.tripDate);
                }
            }
        },
        'stopDetails.customerId': function(newValue, oldValue) {}
    },
    methods: {
        handleBrandListUpdated(brandList) {
            this.brandList = brandList;
        },
        handleCreateCustomer() {
            this.createCustomer({
                ...this.stopDetails,
                load: this.loadValues,
                customFields: this.customFieldValues
            });
        },
        /**
         *
         * @param {import("@/jsDocTypes/FrequentAddress").FrequentAddress} info
         */
        handleFrequentAddressSelected(info) {
            const {
                email,
                contactName,
                phone,
                notes,
                timeWindowStart,
                timeWindowEnd,
                customFields,
                durationMinutes,
                customerId
            } = info;
            // only reset customer if the customerId is not null
            if (customerId) {
                this.stopDetails.customerId = customerId;
            }
            this.stopDetails.contact.email = email;
            this.stopDetails.contact.name = contactName;
            this.stopDetails.contact.phone = phone;

            this.stopDetails.notes = notes;
            if (timeWindowStart || timeWindowEnd) {
                this.stopDetails.appointmentTime = null;
                this.isSetTripDate = true;

                if (timeWindowStart) {
                    const time = moment.utc(timeWindowStart).format(DATE_TYPES.militaryTime);
                    this.stopDetails.timeWindowStart = time.toString();
                }
                if (timeWindowEnd) {
                    const time = moment.utc(timeWindowEnd).format(DATE_TYPES.militaryTime);
                    this.stopDetails.timeWindowEnd = time.toString();
                }

                this.time = this.$_getTime(null, this.stopDetails.timeWindowStart, this.stopDetails.timeWindowEnd);
            }
            if (durationMinutes > 0) {
                this.stopDetails.durationMinutes = durationMinutes;
                this.isFrequentAddressSelected = true;
            }
            if (customFields) {
                this.customFieldValues = JSON.parse(customFields);
            } else {
                this.customFieldValues = {};
            }
            this.isFrequentAddressSelected = true;
        },
        /**
         *
         * @param {Object} placeResult
         * @param {Object} placeResult.location
         * @param {Object} placeResult.addressComponents
         * @param {string} placeResult.name
         * @param {string} placeResult.address
         */
        handlePlaceChanged(placeResult) {
            this.stopDetails.location = placeResult.location;
            this.stopDetails.addressComponents = placeResult.addressComponents;
            this.stopDetails.name = placeResult.name;
            this.stopDetails.address = placeResult.address;
        },
        async createStop() {
            if (!this.validateRequiredFields() || !this.validateRequiredCustomFields()) {
                return;
            }

            if (
                (this.isSetTripDate && this.tripDateRequired && this.stopDetails.tripDate == null) ||
                this.isTimeValid === false
            ) {
                return;
            }

            const { assignToPublicUserId } = this.stopDetails;
            this.$v.stopDetails.$touch();

            if (!this.$v.stopDetails.$invalid) {
                this.$_handleLoaderState(true, 'SAVING...');
                // eslint-disable-next-line eqeqeq
                if (assignToPublicUserId == null || assignToPublicUserId == 0)
                    this.stopDetails.assignToPublicUserId = null;
                // eslint-disable-next-line radix
                this.stopDetails.durationMinutes = parseInt(this.stopDetails.durationMinutes);
                this.stopDetails.customFields = JSON.stringify(this.customFieldValues);
                this.stopDetails.skills =
                    this.stopDetails.skills === 'null' || this.stopDetails.skills === []
                        ? null
                        : this.stopDetails.skills;

                this.stopDetails.chargeLines = this.chargeList;
                this.stopDetails.costLines = this.costList;
                this.stopDetails.stopAppliedChargingRule = this.chargeRule;
                this.stopDetails.stopAppliedCostingRule = this.costRule;

                const api = '/api/stops';
                const payload = {
                    method: 'post',
                    data: this.stopDetails
                };

                try {
                    const responseData = await handleRequests(api, payload);
                    this.$notify({
                        message: 'Successfully created a stop.',
                        type: 'success'
                    });

                    if (responseData) {
                        const { feedbackMessage, stopId, stopRef } = responseData.data;
                        if (
                            feedbackMessage &&
                            typeof feedbackMessage === 'string' &&
                            feedbackMessage.trim().length !== 0
                        ) {
                            this.$notifyWarning(feedbackMessage, 7000);
                        }

                        this.showToast(stopId, stopRef);
                    }

                    this.$v.$reset();
                    this.resolve('ok');
                } catch (e) {
                    const message = 'Could not create a new stop.';
                    showErrorMessage(this, message, e);
                    this.$_handleLoaderState(false);
                }
            }
        },
        getTime(selectedTime, isTimeValid) {
            if (selectedTime != null) {
                const { appointmentTime, timeWindowStart, timeWindowEnd } = selectedTime;
                this.stopDetails.appointmentTime = appointmentTime;
                this.stopDetails.timeWindowStart = timeWindowStart;
                this.stopDetails.timeWindowEnd = timeWindowEnd;
            } else {
                this.stopDetails.appointmentTime = null;
                this.stopDetails.timeWindowStart = null;
                this.stopDetails.timeWindowEnd = null;
            }
            this.isTimeValid = isTimeValid;
        },
        resetStopState() {
            Object.assign(this.stopDetails, STOP_DETAILS_DEFAULTS());
            this.$v.$reset();
        },
        handleCustomFieldChanged({ name, value }) {
            this.customFieldValues[name] = value;
        },
        handleTeamRegionChanged(value) {
            this.stopDetails.teamRegionId = value;
            this.selectedTeamRegionId = value;
        },
        handleTeamRegionMemberChanged(value) {
            if (
                value != null &&
                value.teamMember != null &&
                value.teamMember.rateGroupId != null &&
                this.stopDetails.rateGroupId === null
            ) {
                this.stopDetails.rateGroupId = value.teamMember.rateGroupId;
            }

            this.stopDetails.teamRegionId = value != null ? value.teamRegionId : null;
            this.stopDetails.assignToPublicUserId = value && value.publicUserId ? value.publicUserId : null;
            this.stopDetails.assignToCarrierTeamId = value && value.carrierTeamId ? value.carrierTeamId : null;
            this.validateCanGenerateRates();
        },
        setDurationMinutes(publicUserId) {
            const teamMember = this.members.find((x) => x.publicUserId === publicUserId);
            const defaultStopDurationMinutes =
                teamMember?.defaultStopDurationMinutes ?? this.user.defaultStopDurationMinutes;

            // User user or team default stop duration, if null use value from STOP_DETAILS_DEFAULTS
            this.stopDetails.durationMinutes = defaultStopDurationMinutes ?? STOP_DETAILS_DEFAULTS().durationMinutes;
        },
        handleLoadChanged({ name, value }) {
            this.loadValues[name] = value;
            this.ruleGenerationLoadDetails();
        },
        handleCoordinates(value) {
            this.useCoordinates = value;
            this.$v.$reset();
        },
        openCharges() {
            document.querySelector('.charge-wrapper').classList.toggle('side-panel-open');
            document.querySelector('.charge-wrapper').classList.toggle('side-panel');
        },
        handleRateTypeChanged(rateRule) {
            this.stopDetails.rateGroupId = rateRule?.rateGroupId ?? null;
            this.ruleGenerationDebounce();
        },
        validateCanGenerateRates(addressChanged, rateGroupChanged) {
            if (this.stopDetails.address !== null && this.ratesIsLoading === false) {
                this.generateRates(addressChanged, rateGroupChanged);
            }
        },
        // eslint-disable-next-line func-names
        ruleGenerationDebounce: _.debounce(function() {
            this.validateCanGenerateRates(false, true);
        }, 500),
        // eslint-disable-next-line func-names
        ruleGenerationLoadDetails: _.debounce(function() {
            this.validateCanGenerateRates(false, false);
        }, 1000),
        handleCustomerFieldChange(text) {
            this.ruleGenerationLoadDetails();
        },
        handleCustomerChangeRateGroup(customerRateGroupId) {
            if (this.stopDetails.rateGroupId === null || this.stopDetails.rateGroupId === undefined) {
                this.stopDetails.rateGroupId = customerRateGroupId;
            }
        },
        // eslint-disable-next-line func-names
        handleStopDetailsChanged: _.debounce(function() {
            this.validateCanGenerateRates(true, false);
        }, 500),
        async generateRates(addressChanged, rateGroupChanged) {
            if (!this.hasRateRules) 
                return;

            if (!this.stopDetails.rateGroupId) {
                this.chargeList = [];
                this.costList = [];

                this.stopDetails.chargeLines = this.chargeList;
                this.stopDetails.costLines = this.costList;
                return;
            }

            this.ratesIsLoading = true;

            let response = {
                data: {
                    rateList: null
                }
            };

            try {
                const data = this.$_getStopData(
                    this.stopDetails,
                    this.user,
                    this.loadValues,
                    null, // chargeList
                    null, // costList
                    true, // isCharging
                    this.stopDetails.assignToPublicUserId !== null && this.stopDetails.assignToPublicUserId !== 0, // isCosting
                    false, // reverCharges
                    false, // revertCosts
                    rateGroupChanged,
                    addressChanged
                );

                const payload = {
                    method: 'post',
                    data
                };

                response = await handleRequests('/api/rates/stop-rates', payload);
                if (response.data) {
                    this.rateListData = response.data;
                    this.chargeList = response.data.charge ?? [];
                    this.costList = response.data.cost ?? [];
                    this.currency = this.rateListData?.currency;
                    this.chargeRule = response.data.chargeRule;
                    this.costRule = response.data.costRule;
                }

                this.ratesIsLoading = false;
            } catch (error) {
                const message = `Error generating rates, if issue persists please contact support`;
                showErrorMessage(this, message, null);
                this.ratesIsLoading = false;
            }
        },
        scrollToBottom() {
            if (this.manualIsSetTripDateToggle) {
                this.$nextTick(() => {
                    this.$refs.bottomElement.scrollIntoView({ behavior: 'smooth' });
                });

                this.manualIsSetTripDateToggle = false;
            }
        },
        handleIsSetTripDateChange(val) {
            this.manualIsSetTripDateToggle = val;
        },
        async retrieveRequiredFlags() {
            this.$_handleLoaderState(true);
            const api = `/api/booking-fields-flags`;
            try {
                const response = await handleRequests(api, { method: 'get' });
                this.requiredBookingFieldsFlags = response.data;
                this.requiredFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredStopFieldsFlags,
                    CREATE_STOP_REQUIRED_BOOKING_FIELDS
                );

                this.$_handleLoaderState(false);
            } catch (error) {
                const message = 'Error in retrieving booking fields flags';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        validateRequiredFields() {
            let isValid = true;
            this.stopDetails.load = this.loadValues;
            this.requiredFieldsBitwiseArray.forEach((bitwiseElement) => {
                const field = CREATE_STOP_REQUIRED_BOOKING_FIELDS.find((x) => x.key === bitwiseElement);
                if (field) {
                    const referenceArray = field.ref.split('.');
                    if (referenceArray.length > 1) {
                        if (
                            referenceArray[0] in this.stopDetails &&
                            referenceArray[1] in this.stopDetails[referenceArray[0]] &&
                            !this.stopDetails[referenceArray[0]][referenceArray[1]]
                        ) {
                            isValid = false;
                            const message = `Please fill in the required field: ${field.label}`;
                            showErrorMessage(this, message, null);
                        } else if (this.loadFieldList.some((el) => el.type === referenceArray[1])) {
                            // Checks the load list if load is null
                            if (!this.loadValues[referenceArray[1]]) {
                                isValid = false;
                                const message = `Please fill in the required field: ${field.label}`;
                                showErrorMessage(this, message, null);
                            }
                        }
                    } else if (!this.stopDetails[referenceArray[0]]) {
                        isValid = false;
                        const message = `Please fill in the required field: ${field.label}`;
                        showErrorMessage(this, message, null);
                    }
                }
            });
            return isValid;
        },
        validateRequiredCustomFields() {
            return this.$_validateRequiredCustomFields(this.stopCustomFieldList, this.customFieldValues);
        },
        showToast(stopId, stopRef) {
            const endPoint = '/stops/details';
            const notifyMessage = marked(
                `You can find the created stop by clicking the following link: <a onmouseout="this.style.color='white'" style="color:white !important; text-decoration: underline;" target="_blank" href="${endPoint}/${stopId}">${stopRef}</a>`
            );

            this.$notifySuccess(notifyMessage, 10000);
        }
    },
    data() {
        return {
            disabledDates: (date) => {
                const x = moment(date).format(DATE_TYPES.internationalDate);
                const now = moment().format(DATE_TYPES.internationalDate);
                return !moment(x).isSameOrAfter(now, 'day');
            },
            isSetTripDate: false,
            tripDateRequired: false,
            isTimeValid: true,
            stopCustomFieldList: [],
            customFieldValues: {},
            loadFieldList: [],
            loadValues: {},
            skillOptions: [],
            teamMemberOptions: [],
            useCoordinates: false,
            // selectedTeamRegionId: null,
            defaultTeamMemberOption: {
                publicUserId: 0,
                fullName: 'Unassigned',
                email: null,
                teamRegionId: null,
                teamId: null
            },
            rateListData: null,
            currency: '',
            chargeList: [],
            ratesIsLoading: false,
            costList: [],
            stopTypes: STOP_TYPES,
            manualIsSetTripDateToggle: false,
            isFrequentAddressSelected: false,
            requiredBookingFieldsFlags: {
                requiredStopFieldsFlags: 0,
                requiredShipmentFieldsFlags: 0,
                requiredShipmentPickupFieldsFlags: 0,
                requiredShipmentDropoffFieldsFlags: 0
            },
            brandList: [],
            time: 'None',
            chargeRule: null,
            costRule: null,
            FEATURE_NAMES
        };
    },
    validations: {
        stopDetails: {
            // eslint-disable-next-line func-names
            address: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return !this.useCoordinates;
                    }),
                coordinatesValid(data) {
                    if (this.useCoordinates) {
                        return isAValidCoordinate(data);
                    }
                    return true;
                }
            },
            durationMinutes: { required, numeric },
            sourceReference: {
                maxLength: maxLength(200)
            }
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .md-menu-content.md-select-menu {
    width: 150px;
}
.modal-container {
    max-width: 800px;
}

.two-column {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
}

.one-column {
    display: grid;
    grid-template-columns: 1fr;
}

.chargeButton-toggle {
    float: right;
    border: none;
    background-color: #4caf50;
    position: relative;
    place-items: center;
    display: grid;
    color: white !important;
}

.chargeButton-toggle:hover {
    background: #409443;
}

.charge-wrapper {
    padding-left: 25px;
}

.trip-date {
    .md-layout-item {
        padding: 0;
        margin: 0;
    }
}
</style>
